import { useEffect, useMemo, useRef, useState } from 'react'
import type { Asset } from '@shared/types'
import { AudioLines, ChevronDown, ChevronRight, Loader2, AlertCircle, Pencil, Plus, ScanSearch, Trash2, X } from 'lucide-react'
import { api } from '../../../api'
import { useEditorStore } from '../../../store/editorStore'
import { useUiStore } from '../../../store/uiStore'
import { assetDisplayName } from '../../../lib/assetName'
import { formatTime } from '../../../lib/time'
import { Button } from '../../common/Button'
import { SliderField } from '../../common/Field'
import { Menu, MenuItem, MenuPanel } from '../../common/Menu'
import { Panel } from '../../common/Panel'
import { SceneChip } from './SceneChip'
import { computeInsertIndexAtPlayhead } from './insertAt'

/**
 * UI_MK2_PHASE1.md §3-3 "アップロード進捗": ingest 中の実際の割合。`uploads`
 * (byte-level アップロード進捗) → `ingestJobs`（サーバ側の解析 Job.progress）の
 * 順で拾う。どちらも無ければ割合なしの「取込中…」のまま（捏造しない）。
 *
 * P10-2 (docs/P10_PHASE2.md §2 item 4, 担当A): `ingestJobs` also holds
 * 'redetect' Jobs for this asset (editorStore.redetectAsset appends there,
 * not a separate map — "進捗（既存の ingest 進捗表示を流用）"), filtered by
 * `job.kind` here so a stray Job of some other kind sharing this assetId
 * (shouldn't happen today, but cheap to assert) can never show a bogus
 * overlay.
 *
 * reviewer 6/10 A-1 (MAJOR): the previous version looked up `entry` in
 * `uploads` FIRST and `return null`ed the instant it was missing/done/error
 * — but `uploads` entries are created ONLY by `uploadFiles` (the initial
 * upload flow). `redetectAsset` never touches `uploads` at all, so for a
 * re-detect run this hook always hit that early `return null` and the
 * ingest-Job lookup a few lines below was unreachable — 「シーン再検出」never
 * showed ANY progress overlay. Fixed order: uploads' `'uploading'` status
 * (byte progress) takes priority WHEN PRESENT, but its absence no longer
 * short-circuits the ingestJobs lookup.
 */
function useIngestOverlay(assetId: string): { label: string; pct: number | null } | null {
  const uploads = useEditorStore((s) => s.uploads)
  const ingestJobs = useEditorStore((s) => s.ingestJobs)
  // reviewer 6/10 A-1: byte-level upload progress takes priority WHEN
  // present, but its absence must not short-circuit the whole hook anymore
  // — the previous version `return null`-ed here unless `uploads` had a
  // non-done/error entry for this asset, which `uploadFiles` is the ONLY
  // action that ever creates. `redetectAsset` never touches `uploads` at
  // all (it only writes into `ingestJobs`), so a redetect Job's progress
  // was unreachable code below this line and「シーン再検出」never showed any
  // overlay at all.
  const uploading = Object.values(uploads).find((u) => u.assetId === assetId && u.status === 'uploading')
  if (uploading) {
    const pct = uploading.total > 0 ? Math.round((uploading.loaded / uploading.total) * 100) : null
    return { label: pct != null ? `アップロード中 ${pct}%` : 'アップロード中…', pct }
  }
  const job = Object.values(ingestJobs).find(
    (j) =>
      j.assetId === assetId &&
      (j.kind === 'ingest' || j.kind === 'redetect') &&
      (j.status === 'running' || j.status === 'queued'),
  )
  if (!job) return null
  const pct = Math.round(job.progress * 100)
  return { label: pct != null ? `解析中 ${pct}%` : '解析中…', pct }
}

/**
 * P10-2 (docs/P10_PHASE2.md §2 item 4): a redetect Job (kind:'redetect') for
 * THIS asset is queued/running — used to disable「シーン再検出」in the card
 * menu while one is already in flight (the server would 409 `asset_busy`
 * anyway; disabling client-side just avoids a pointless round trip).
 */
function useAssetBusy(assetId: string): boolean {
  const ingestJobs = useEditorStore((s) => s.ingestJobs)
  return Object.values(ingestJobs).some(
    (j) => j.assetId === assetId && (j.status === 'running' || j.status === 'queued'),
  )
}

function IngestOverlay({ overlay }: { overlay: { label: string; pct: number | null } }) {
  return (
    <div
      className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center gap-1 px-1"
      data-testid="asset-ingest-overlay"
    >
      <div className="text-[9px] text-white text-center leading-tight">{overlay.label}</div>
      {overlay.pct != null && (
        <div className="w-3/4 h-1 rounded bg-white/20 overflow-hidden">
          <div className="h-full bg-accent-blue" style={{ width: `${overlay.pct}%` }} />
        </div>
      )}
    </div>
  )
}

function AddButton({ onClick, className = '' }: { onClick: (e: React.MouseEvent) => void; className?: string }) {
  return (
    <button
      onClick={onClick}
      title="タイムライン末尾に先頭シーンを追加（⌥＋クリックで再生ヘッド位置に挿入）"
      data-testid="asset-card-add"
      className={`w-6 h-6 rounded-full bg-black/70 border border-white/30 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-accent-blue/80 hover:border-accent-blue ${className}`}
    >
      <Plus size={13} />
    </button>
  )
}

/**
 * P10-2 (docs/P10_PHASE2.md §2 item 2): inline rename over the asset's
 * DISPLAY name (`assetDisplayName`, i.e. `label ?? filename`) — Enter
 * commits, Esc cancels. Unlike ProjectList's `RenameInput` (which rejects an
 * empty value outright), an EMPTY trimmed value here is a legitimate,
 * intentional action: "空文字で label を削除→filename 表示に戻る" (task spec
 * item 2) — so it's committed as `renameAsset(id, '')`, not treated as a
 * no-op/cancel.
 */
function RenameAssetInput({ asset, onDone }: { asset: Asset; onDone: () => void }) {
  const renameAsset = useEditorStore((s) => s.renameAsset)
  const [value, setValue] = useState(assetDisplayName(asset))
  const [saving, setSaving] = useState(false)

  async function commit() {
    const trimmed = value.trim()
    if (trimmed === assetDisplayName(asset)) {
      onDone()
      return
    }
    setSaving(true)
    try {
      await renameAsset(asset.id, trimmed)
    } catch {
      // best-effort, same convention as ProjectList's RenameInput — a
      // failed rename (network hiccup, 404 if deleted elsewhere) just
      // reverts silently; nothing else in the UI depended on it landing.
    }
    onDone()
  }

  return (
    <input
      autoFocus
      data-testid="asset-rename-input"
      value={value}
      disabled={saving}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          onDone()
        }
      }}
      className="w-full rounded bg-ink-bg/70 border border-accent-blue/60 px-1.5 py-0.5 text-xs text-text focus:outline-none"
    />
  )
}

/**
 * Extracts the server's own human-readable `detail` sentence out of a
 * thrown API error's `.message` — httpClient.ts's `req()` builds messages as
 * `` `${method} ${path} -> ${status} ${detail}` ``, mockClient.ts's
 * `mockHttpError` as `` `${code}: ${detail} (mock)` ``. Both wrap the SAME
 * server-authored sentence (e.g. routes/assets.ts DELETE's `` `構成案『X』
 * 『Y』で使用中のため削除できません` ``, which names the ACTUAL compositions —
 * necessarily different per asset, so it can't be a single static string
 * the way common/ConfirmDialog's `busyMessage` prop is for other flows).
 * Falls back to the raw message when neither shape matches.
 */
function extractApiErrorDetail(message: string): string {
  const arrow = message.match(/-> \d+ (.*)$/s)
  if (arrow) return arrow[1]!.trim()
  const coded = message.match(/^[a-z_]+:\s*(.*)$/s)
  if (coded) return coded[1]!.replace(/\s*\(mock\)\s*$/, '').trim()
  return message
}

/**
 * P10-2 reviewer 6/10 A-6 (minor): shared keyboard/a11y wiring for
 * AssetDeleteDialog/RedetectDialog (both self-built, not common/
 * ConfirmDialog — see `extractApiErrorDetail`'s doc comment for why).
 * Returns a ref for the dialog panel: on mount, focuses the first
 * non-disabled `<button>` inside it (so a keyboard user isn't left tabbing
 * from the page body); for the lifetime of the dialog, Escape calls
 * `onClose` unless `disabled` (a submit in flight — matches the existing
 * click-outside-to-close guard both dialogs already had).
 * `common/ConfirmDialog` has neither of these either (a pre-existing gap,
 * shared with Header's/ProjectList's dialogs) — out of scope to fix here
 * since it's a shared component other担当 also use; kept local instead.
 */
function useDialogA11y(onClose: () => void, disabled: boolean) {
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const first = panelRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')
    first?.focus()
    // mount-only: re-focusing on every `disabled` flip would yank focus
    // away from wherever the user tabbed to mid-dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !disabled) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose, disabled])

  return panelRef
}

/** P10-2 item 3: delete confirmation, self-built (not common/ConfirmDialog) so a 409's exact server detail — which composition(s) — can be shown verbatim; see `extractApiErrorDetail`'s doc comment. */
function AssetDeleteDialog({ asset, usedCount, onClose }: { asset: Asset; usedCount: number; onClose: () => void }) {
  const deleteAsset = useEditorStore((s) => s.deleteAsset)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const panelRef = useDialogA11y(onClose, submitting)

  async function handleConfirm() {
    setSubmitting(true)
    setError(null)
    try {
      await deleteAsset(asset.id)
      // success: caller (AssetCard) unmounts this dialog by clearing its own state
    } catch (e) {
      setError(extractApiErrorDetail((e as Error)?.message ?? String(e)))
      setSubmitting(false)
    }
  }

  return (
    <div
      ref={panelRef}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60"
      data-testid="asset-delete-dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose()
      }}
    >
      {/* Panel is a plain function component (no forwardRef) — `useDialogA11y`'s ref goes on this backdrop div instead, one level up; its `querySelector` still finds every button inside Panel just as well since it only needs to be an ANCESTOR, not the exact dialog surface. */}
      <Panel
        role="dialog"
        aria-modal="true"
        aria-label={`『${assetDisplayName(asset)}』を削除しますか？`}
        className="w-[360px] p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2.5 mb-2">
          <AlertCircle size={18} className="text-accent-red shrink-0 mt-0.5" />
          <div className="text-sm font-medium flex-1">『{assetDisplayName(asset)}』を削除しますか？</div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-text-faint hover:text-text disabled:opacity-40"
            data-testid="asset-delete-dialog-close"
            aria-label="閉じる"
          >
            <X size={16} />
          </button>
        </div>
        <div className="text-xs text-text-muted mb-4 whitespace-pre-wrap">
          {usedCount > 0
            ? `タイムラインで${usedCount}回使用されています。素材ファイルも完全に削除され、元に戻せません。`
            : '素材ファイルも完全に削除されます。元に戻せません。'}
        </div>
        {error && (
          <div
            className="text-[11px] text-accent-red mb-3 break-words rounded bg-accent-red/10 border border-accent-red/30 px-2.5 py-2"
            data-testid="asset-delete-dialog-error"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting} data-testid="asset-delete-dialog-cancel">
            キャンセル
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleConfirm}
            disabled={submitting}
            data-testid="asset-delete-dialog-confirm"
          >
            {submitting ? '処理中…' : '削除'}
          </Button>
        </div>
      </Panel>
    </div>
  )
}

/** P10-2 item 4: threshold slider + 実行 — the run itself is fire-and-forget from this dialog's point of view (progress shows on the card via the existing ingest overlay, `useIngestOverlay` above); this only surfaces the immediate 409 (`asset_busy`) if the request itself is refused. */
function RedetectDialog({ asset, onClose }: { asset: Asset; onClose: () => void }) {
  const redetectAsset = useEditorStore((s) => s.redetectAsset)
  const [threshold, setThreshold] = useState(0.3)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const panelRef = useDialogA11y(onClose, submitting)

  async function handleRun() {
    setSubmitting(true)
    setError(null)
    try {
      await redetectAsset(asset.id, threshold)
      onClose()
    } catch (e) {
      setError(extractApiErrorDetail((e as Error)?.message ?? String(e)))
      setSubmitting(false)
    }
  }

  return (
    <div
      ref={panelRef}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60"
      data-testid="redetect-dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose()
      }}
    >
      <Panel role="dialog" aria-modal="true" aria-label="シーン再検出" className="w-[320px] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-2.5 mb-1">
          <div className="text-sm font-medium flex-1">シーン再検出</div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-text-faint hover:text-text disabled:opacity-40"
            data-testid="redetect-dialog-close"
            aria-label="閉じる"
          >
            <X size={16} />
          </button>
        </div>
        <div className="text-[11px] text-text-muted mb-3">
          現在 {asset.scenes.length} シーン。しきい値を下げるとカット数が増え、上げると減ります。
        </div>
        <SliderField
          label="検出しきい値"
          value={threshold}
          min={0.1}
          max={0.6}
          step={0.05}
          onChange={setThreshold}
          data-testid="redetect-threshold-slider"
        />
        {error && (
          <div
            className="text-[11px] text-accent-red mt-3 break-words rounded bg-accent-red/10 border border-accent-red/30 px-2.5 py-2"
            data-testid="redetect-dialog-error"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting} data-testid="redetect-dialog-cancel">
            キャンセル
          </Button>
          <Button variant="primary" size="sm" onClick={handleRun} disabled={submitting} data-testid="redetect-run">
            {submitting ? '実行中…' : '実行'}
          </Button>
        </div>
      </Panel>
    </div>
  )
}

/** P10-2 items 2-4: the card's own「…」menu — 名前を変更／シーン再検出／削除. Shared between grid and list layouts. */
function AssetCardMenu({
  asset,
  isAudio,
  busy,
  onRename,
  onDelete,
  onRedetect,
}: {
  asset: Asset
  isAudio: boolean
  busy: boolean
  onRename: () => void
  onDelete: () => void
  onRedetect: () => void
}) {
  return (
    <Menu
      testId="asset-menu"
      trigger={({ toggle }) => (
        <button
          type="button"
          onClick={toggle}
          className="w-5 h-5 shrink-0 rounded flex items-center justify-center text-text-faint hover:text-text hover:bg-white/10 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
          aria-label="素材メニュー"
          data-testid="asset-menu-trigger"
        >
          <span className="text-sm leading-none">⋯</span>
        </button>
      )}
    >
      {({ close }) => (
        <MenuPanel>
          <MenuItem
            icon={<Pencil size={13} />}
            testId="asset-menu-rename"
            onSelect={() => {
              close()
              onRename()
            }}
          >
            名前を変更
          </MenuItem>
          {!isAudio && (
            <MenuItem
              icon={<ScanSearch size={13} />}
              testId="asset-menu-redetect"
              disabled={asset.ingestStatus !== 'ready' || busy}
              title={
                asset.ingestStatus !== 'ready'
                  ? '取込完了後に実行できます'
                  : busy
                    ? '処理中のため実行できません'
                    : undefined
              }
              onSelect={() => {
                close()
                onRedetect()
              }}
            >
              シーン再検出
            </MenuItem>
          )}
          <MenuItem
            icon={<Trash2 size={13} />}
            danger
            testId="asset-menu-delete"
            onSelect={() => {
              close()
              onDelete()
            }}
          >
            削除
          </MenuItem>
        </MenuPanel>
      )}
    </Menu>
  )
}

export function AssetCard({ asset }: { asset: Asset }) {
  const expanded = useEditorStore((s) => !!s.expandedAssetIds[asset.id])
  const toggle = useEditorStore((s) => s.toggleAssetExpanded)
  const viewMode = useUiStore((s) => s.assetBinViewMode)
  const setHoverAssetId = useUiStore((s) => s.setHoverAssetId)
  const addSegmentFromScene = useEditorStore((s) => s.addSegmentFromScene)

  // Audio-only asset (BGM extracted with `ffmpeg -vn`, ingested with
  // width=0/height=0 — server/src/ingest/pipeline.ts). Only meaningful once
  // ready: a *video* upload is also width=0 while still pending/processing
  // (probe hasn't run yet), so checking this before 'ready' would
  // misclassify every in-flight video upload as audio.
  const isAudio = asset.ingestStatus === 'ready' && asset.width === 0 && asset.height === 0

  const usedCount = useEditorStore((s) =>
    s.composition ? s.composition.tracks.video.filter((seg) => seg.assetId === asset.id).length : 0,
  )

  const ingestOverlay = useIngestOverlay(asset.id)
  const busy = useAssetBusy(asset.id)

  // P10-2 items 2-4: card-local UI state (rename input / delete confirm /
  // redetect dialog). None of this is persisted Composition state — it's
  // purely which modal/inline-edit this ONE card currently shows.
  const [renaming, setRenaming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [redetecting, setRedetecting] = useState(false)

  const firstScene = useMemo(() => asset.scenes[0] ?? null, [asset.scenes])

  // The card's own thumbnail toggles scene-strip expand/collapse and also
  // hosts the "+" AddButton on hover — an interactive <button> nested inside
  // another <button> is invalid HTML (React's direct-DOM-API rendering lets
  // it "work" at runtime, but it's still wrong), so the toggle wrapper is a
  // role="button" div with its own keyboard handling instead.
  function handleToggleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggle(asset.id)
    }
  }

  function handleCardAdd(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (!firstScene) return
    // UI_MK2_PHASE2.md §2-7 (reviewer minor): `playback.currentSec` を購読すると
    // このカードが再生中は毎フレーム（30fps）再レンダーされる — クリック時
    // だけ getState() で読めば十分（購読は不要）。
    const store = useEditorStore.getState()
    const insertAtIdx = e.altKey
      ? computeInsertIndexAtPlayhead(store.composition?.tracks.video ?? [], store.playback.currentSec)
      : undefined
    addSegmentFromScene(asset.id, firstScene, insertAtIdx)
  }

  const canAdd = asset.ingestStatus === 'ready' && !isAudio && firstScene

  const thumbInner = (
    <>
      {isAudio ? (
        <div className="w-full h-full flex items-center justify-center" data-testid="asset-audio-icon">
          <AudioLines size={18} className="text-accent-blue" />
        </div>
      ) : asset.thumbPath ? (
        <img src={api.fileUrl(asset.thumbPath)} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          {asset.ingestStatus === 'error' ? (
            <AlertCircle size={14} className="text-accent-red" />
          ) : (
            <Loader2 size={14} className="text-text-faint animate-spin" />
          )}
        </div>
      )}
      {ingestOverlay && <IngestOverlay overlay={ingestOverlay} />}
      {usedCount > 0 && (
        <div
          className="absolute top-1 left-1 min-w-[16px] h-4 px-1 rounded-full bg-accent-blue/90 text-[9px] text-ink-bg font-semibold flex items-center justify-center tabular-nums"
          title={`タイムラインで${usedCount}回使用中`}
          data-testid="asset-used-badge"
        >
          {usedCount}
        </div>
      )}
      {canAdd && <AddButton onClick={handleCardAdd} className="absolute bottom-1 right-1" />}
    </>
  )

  const sceneStrip =
    expanded && asset.ingestStatus === 'ready' && isAudio ? (
      <div className="px-2 pb-2 text-[10px] text-text-faint">
        音声素材です（BGM候補・シーン検出対象外）。「音」タブから BGM として追加できます。
      </div>
    ) : expanded && asset.ingestStatus === 'ready' && !isAudio ? (
      <div className="flex gap-1.5 px-2 pb-2 overflow-x-auto no-scrollbar">
        {asset.scenes.length === 0 ? (
          <div className="text-[10px] text-text-faint py-2">シーンが検出されませんでした</div>
        ) : (
          asset.scenes.map((scene) => <SceneChip key={scene.index} assetId={asset.id} scene={scene} />)
        )}
      </div>
    ) : expanded && asset.ingestStatus === 'processing' ? (
      <div className="px-2 pb-2 text-[10px] text-text-faint">取込中…</div>
    ) : expanded && asset.ingestStatus === 'error' ? (
      <div className="px-2 pb-2 text-[10px] text-accent-red">{asset.ingestError ?? '取込に失敗しました'}</div>
    ) : null

  const menu = (
    <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <AssetCardMenu
        asset={asset}
        isAudio={isAudio}
        busy={busy}
        onRename={() => setRenaming(true)}
        onDelete={() => setDeleting(true)}
        onRedetect={() => setRedetecting(true)}
      />
    </div>
  )

  const dialogs = (
    <>
      {deleting && <AssetDeleteDialog asset={asset} usedCount={usedCount} onClose={() => setDeleting(false)} />}
      {redetecting && <RedetectDialog asset={asset} onClose={() => setRedetecting(false)} />}
    </>
  )

  if (viewMode === 'grid') {
    return (
      <div
        className="group rounded border border-ink-border bg-ink-surface2/60 overflow-hidden"
        data-testid="asset-card"
        data-view-mode="grid"
        onMouseEnter={() => setHoverAssetId(asset.id)}
        onMouseLeave={() => setHoverAssetId(null)}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggle(asset.id)}
          onKeyDown={handleToggleKeyDown}
          className="w-full text-left cursor-pointer"
          title={assetDisplayName(asset)}
        >
          <div className="relative w-full aspect-[9/16] bg-ink-bg overflow-hidden">{thumbInner}</div>
          <div className="px-1.5 py-1 flex items-center gap-1">
            {expanded ? (
              <ChevronDown size={12} className="text-text-faint shrink-0" />
            ) : (
              <ChevronRight size={12} className="text-text-faint shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              {renaming ? (
                <RenameAssetInput asset={asset} onDone={() => setRenaming(false)} />
              ) : (
                <div className="text-[11px] font-medium truncate">{assetDisplayName(asset)}</div>
              )}
              <div className="text-[9px] text-text-faint">
                {asset.ingestStatus === 'ready' ? (
                  formatTime(asset.durationSec)
                ) : asset.ingestStatus === 'error' ? (
                  <span className="text-accent-red">取込エラー</span>
                ) : (
                  '取込中…'
                )}
              </div>
            </div>
            {!renaming && menu}
          </div>
        </div>
        {sceneStrip}
        {dialogs}
      </div>
    )
  }

  return (
    <div
      className="group rounded border border-ink-border bg-ink-surface2/60 overflow-hidden"
      data-testid="asset-card"
      data-view-mode="list"
      onMouseEnter={() => setHoverAssetId(asset.id)}
      onMouseLeave={() => setHoverAssetId(null)}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => toggle(asset.id)}
        onKeyDown={handleToggleKeyDown}
        className="w-full flex items-center gap-2 p-2 text-left cursor-pointer hover:bg-white/5"
      >
        {expanded ? (
          <ChevronDown size={14} className="text-text-faint shrink-0" />
        ) : (
          <ChevronRight size={14} className="text-text-faint shrink-0" />
        )}
        <div className="w-10 h-[71px] rounded overflow-hidden bg-ink-bg shrink-0 relative">{thumbInner}</div>
        <div className="min-w-0 flex-1">
          {renaming ? (
            <RenameAssetInput asset={asset} onDone={() => setRenaming(false)} />
          ) : (
            <div className="text-xs font-medium truncate">{assetDisplayName(asset)}</div>
          )}
          <div className="text-[10px] text-text-faint mt-0.5">
            {asset.ingestStatus === 'ready' ? (
              isAudio ? (
                <>音声 · {formatTime(asset.durationSec)}</>
              ) : (
                <>
                  {formatTime(asset.durationSec)} · {asset.width}x{asset.height}
                </>
              )
            ) : asset.ingestStatus === 'error' ? (
              <span className="text-accent-red">取込エラー</span>
            ) : (
              '取込中…'
            )}
          </div>
        </div>
        {!renaming && menu}
      </div>
      {sceneStrip}
      {dialogs}
    </div>
  )
}
